import { useCallback, useEffect } from 'react'
import clsx from 'clsx'

import { getTypographyTokenStyle, parseCornerRadius } from '@makyoapp/makyo-tw-ui'

import { CoreComponentFormProps, CoreComponentInfos } from '@/core/models/app-components/types'
import { parseStdColumn } from '@/core/models/columns/parsers/parser'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { ComponentSettingKey } from '@/shared/builder/types'
import { EVENT_KEYS } from '@/shared/custom-event'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { useUpdateFormDefaultValue } from '../../../hooks/useUpdateFormDefaultValue'
import { CompHiddenFieldProps } from './comp'

export interface CompHiddenFieldWrapperProps {
  infos: CoreComponentInfos
  form: CoreComponentFormProps
  isPreview: boolean // for [app builder]: true, [public app]: false
  children: (props: CompHiddenFieldProps) => React.ReactNode
}

/**
 * CompHiddenFieldWrapperCore
 */
export const CompHiddenFieldWrapperCore = ({
  infos: { settings, containerClient, detailTableId, detailRowIndex, currentParentBg, component },
  form,
  isPreview,
  children,
}: CompHiddenFieldWrapperProps) => {
  const { openComponentSettings, isDevelopmentMode } = useAppBuilderContext()

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // ========================= form settings =========================
  const { dataSourceTable, formState, setFormState, formSettings, formId } = form

  // ========================= data =========================
  const columnId = settings?.[ComponentSettingKey.HiddenFieldDataValue] as string
  const column = dataSourceTable?.columnsMap.get(columnId)
  const columnName = column?._Name

  const defaultValue = useParseVariableSelect(
    settings[ComponentSettingKey.HiddenFieldDataDefaultValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  // ========================= design =========================
  const labelTypoStyle = getTypographyTokenStyle(formSettings.labelTypo)

  // ========================= form state =========================
  const value = formState[columnId] as any

  const handleUpdateDefaultValue = useCallback(
    (defValue: any) => {
      setFormState((prev) => ({ ...prev, [columnId]: defValue }))
    },
    [columnId, setFormState]
  )

  useUpdateFormDefaultValue({
    defaultValue,
    value: formState[columnId],
    onUpdate: handleUpdateDefaultValue,
  })

  // recalculate default value when form reset
  useEffect(() => {
    const handleFormReset = (e: Event) => {
      if ((e as CustomEvent).detail.formId === formId) {
        handleUpdateDefaultValue(defaultValue)
      }
    }
    window.addEventListener(EVENT_KEYS.BUILDER_FORM_RESET_HIDDEN_FIELD, handleFormReset)

    return () => {
      window.removeEventListener(EVENT_KEYS.BUILDER_FORM_RESET_HIDDEN_FIELD, handleFormReset)
    }
  }, [defaultValue, formId, handleUpdateDefaultValue])

  // ========================= render =========================
  const renderCurrentValue = useCallback(() => {
    if (typeof value === 'undefined' || value === null || !column || !dataSourceTable) {
      return null
    }

    const cell = parseStdColumn(
      column._Type,
      column._Settings,
      value,
      dataSourceTable.columnsMap,
      undefined,
      undefined,
      {
        timezone: dataSourceTable?.settings._Tz,
      }
    )

    return <>: {cell.toString()}</>
    //
  }, [column, value, dataSourceTable])

  const hiddenFieldProps: CompHiddenFieldProps = {
    isPreview,
    columnName,
    currentValue: renderCurrentValue(),
    backgroundColor: currentParentBg,
    style: {
      borderRadius: parseCornerRadius(formSettings.cornerRadius),
      font: labelTypoStyle.font,
    },
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(hiddenFieldProps)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return <div className={clsx('mk-form-field', { 'is-hidden': !isPreview })}>{children(hiddenFieldProps)}</div>
}
